import { Transition } from '@headlessui/react';
import { RequestStatuses } from '@oncolens/oncolens-ui/lib/dto/event/request';
import { useCollaborationRequest } from '@oncolens/oncolens-ui/lib/hooks/collaboration-requests/useCollaborationRequest';
import { useNavigate, useParams } from 'react-router-dom';
import { CommentsSection, DicomSection } from '../../../requests';
import { CaseInformation } from '../components/CaseInformation';
import { CaseRecommendation } from '../components/CaseRecommendation';
import { RequestInformation } from '../components/RequestInformation';
import { ReferringProviderInformation } from '../components/ReferringProviderInformation';
import { EventHistory } from '../components/EventHistory';
import { TreatmentHistory } from '../components/TreatmentHistory';
import { useRequestDiseases } from '@/features/requests/api/getRequestDiseases';
import { PatientHistory } from '@/features/requests/ViewRequest/components/PatientHistory';
import RequestDiseases from '@/features/requests/ViewRequest/components/RequestDiseases';
import RequestBiomarkerResults from '@/features/requests/ViewRequest/components/RequestBiomarkerResults';
import { useWorkflow, WorkflowUtilities } from "@/features/requests/api/getWorkflow";
import RequestSection from "@/features/requests/components/NewRequest/RequestSection";
import { AttachmentsInformation } from "@/features/requests/ViewRequest/components/AttachmentsInformation";
import { useGetTemplates } from "@/features/requests/api/getTemplates";
import { isEmpty } from "lodash";

interface ViewRequestParams {
	[x: string]: string;
	requestId: string;
}

export const ViewRequest = () => {
  const { requestId } = useParams<ViewRequestParams>();
  const navigate = useNavigate();
  const { data } = useCollaborationRequest({
    id: requestId || '',
    include: ['Patient', 'Diseases', 'CreatedBy', 'LastUpdatedBy'],
    options: {
      refetchOnWindowFocus: true,
    }
  });
  const diseases = useRequestDiseases(requestId || '');
  
  const { data: workflow } = useWorkflow({
    id: data?.workflowId || '',
    options: {
      enabled: !!data?.workflowId
    }
  });
  const { data: templateList } = useGetTemplates();
  const template = templateList?.find((template) => template.workflowId === workflow?.id);

  if (!requestId) navigate('/requests', { replace: true });
  
  const showDiseases = !isEmpty(diseases.data); 
  const showPatientHistory = !isEmpty(diseases.data);
  const showReferringProviderInformation = data?.referringProviderEmail || data?.referringProviderName || data?.referringProviderPhone;
  // TODO: Move these to Workflow options?
  const [showBiomarkers, showEventHistory, showTreatmentHistory] = new Array(3).fill(template?.component === "OsuFormTemplate");

  return (
    <Transition
      enter="transition-opacity duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      show={true}
      appear={true}
    >
      <RequestInformation requestId={requestId || ''} request={data} templateId={template?.id ?? ''} />
      {showReferringProviderInformation && <ReferringProviderInformation eventData={data} />}
      <CaseRecommendation eventData={data} />
      <CaseInformation eventData={data} />
      {showDiseases && <RequestDiseases diseases={diseases.data} /> }
      {showBiomarkers && <RequestBiomarkerResults patientId={data?.patient?.id} />}
      {showEventHistory && <EventHistory patientId={data?.patient?.id} />}
      {WorkflowUtilities.shouldShowImagingAndAttachmentsSection(workflow) &&
        (<RequestSection title="Imaging" className="print:hidden">
              <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
                  <div className="">
                      <h3 className="mb-2 text-lg">Radiology Images</h3>
                      <DicomSection
                          readonly={true}
                          patientId={data?.patient?.id}
                          refetchIntervalMs={false}
                          emptyText={"You don't have any radiology studies."}
                      />
                  </div>
                {WorkflowUtilities.shouldShowAttachments(workflow) &&
                  (<AttachmentsInformation requestId={requestId || ''} />)}
              </div>
          </RequestSection>)
      }

      {showPatientHistory && <PatientHistory disease={diseases.data?.find((d) => d.isPrimary)} />}
      {showTreatmentHistory && <TreatmentHistory patientId={data?.patient?.id} />}
      <CommentsSection requestId={requestId || ''} />
      {data?.status === RequestStatuses.ReportReady && (
        <>
          <hr className="mb-2" />
          <span className="block mb-4 text-sm" data-cy="recommendation-text">
						The reviewing expert providing the guidance does not have the benefit of information
						that would be obtained by directly examining the patient, obtaining the patient&apos;s
						history, and observing the patient&apos;s physical condition. Therefore, the reviewing
						expert may not be aware of facts or information that would affect the guidance. As such,
						the guidance provided above is intended only for physician education.The guidance does
						not constitute a formal consultation, second opinion, diagnosis, medical advice,
						treatment, or medical care, and it does not establish a physician-patient relationship.
						The treating physician shall remain at all times solely responsible for the diagnosis,
						care, treatment, and evaluation of the guidance.
          </span>
        </>
      )}
    </Transition>
  );
};
